<script lang="ts" setup>
import KlereoIcon from "@/shared/components/KlereoIcon.vue";
import { computed, inject, ref } from "vue";
import KlereoModal from "@/shared/components/KlereoModal.vue";
import RenameIOForm from "@/features/probe/RenameIOForm.vue";
import { getRename } from "@/shared/utils/renames";
import { usePool } from "@/features/pool/usePool";
import { useIsStuck } from "@/shared/utils/useIsStuck.ts";
import { triggerPoolsRefresh } from "@/features/pools/useSidebarRefresh.ts";

const props = defineProps({
  leftIcon: String,
  leftLink: String,
  leftClass: String,
  rightIcon: String,
  rightLink: String,
  rightClass: String,
  title: String,
  description: String,
  ioType: {
    required: false,
    type: Number,
  },
  ioIndex: {
    required: false,
    type: Number,
  },
});

const renameModalOpen = ref(false);

const pool = usePool();
const refreshPool = inject("refreshPool") as () => Promise<void> | undefined;

const rename = computed(
  () =>
    (props.ioType !== undefined &&
      props.ioIndex !== undefined &&
      getRename(pool, props.ioType, props.ioIndex)) ||
    undefined
);

const headerRef = ref<HTMLElement | null>(null);
const { isStuck } = useIsStuck(headerRef);

// Rafraîchit la pool courante et, en parallèle, la liste des bassins de la
// sidebar (au cas où des bassins Bluetooth soient apparus).
const onRefresh = () => {
  refreshPool?.();
  triggerPoolsRefresh();
};
</script>

<template>
  <div
    ref="headerRef"
    class="-mx-5 mb-0 -mt-[calc(1.25rem_+_env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] sticky top-0 z-10 bg-transparent transition-all border-transparent border-b"
    :class="{
      'bg-background backdrop-blur-lg bg-opacity-60  border-b-gray-300/50':
        isStuck,
    }"
  >
    <klereo-modal :opened="renameModalOpen" @close="renameModalOpen = false">
      <rename-i-o-form
        :io-type="ioType!"
        :io-index="ioIndex!"
        :default-label="title"
        @close="renameModalOpen = false"
      />
    </klereo-modal>
    <div class="p-5">
      <div class="flex text-white justify-between items-start">
        <div class="flex gap-1 lg:basis-1/6">
          <div
            class="bigger-clickable-area cursor-pointer"
            v-if="leftIcon"
            :class="leftClass + ' w-[27px] bigger-clickable-area'"
            @click="leftLink ? $router.replace(leftLink) : $emit('left')"
          >
            <klereo-icon :name="leftIcon" />
          </div>
          <slot></slot>
        </div>
        <div
          class="flex gap-2 align-items-center text-lg text-center"
          @click="$emit('title-click', $event)"
        >
          {{ rename ?? title }}
          <klereo-icon
            v-if="pool?.IORename && ioType && ioType > 0"
            class="cursor-pointer"
            name="edit"
            @click="renameModalOpen = true"
            style="width: 15px"
          />
        </div>
        <div class="flex gap-5 items-center lg:basis-1/6 justify-end">
          <klereo-icon
            v-if="rightIcon"
            :name="rightIcon"
            :class="rightClass + ' cursor-pointer w-[27px]'"
            @click="rightLink ? $router.push(rightLink) : $emit('right')"
          />
          <slot name="right-section" />
          <klereo-icon
            v-if="refreshPool"
            @click="onRefresh"
            name="refresh"
            class="cursor-pointer w-[22px]"
          />
        </div>
      </div>
      <div v-if="description" class="text-lightblue text-[11px] text-center">
        {{ description }}
      </div>
    </div>
  </div>
</template>
